/**
 * A confirmação das operações que não têm volta.
 *
 * **Pede para digitar o nome do alvo**, não uma caixinha. Caixinha se marca sem
 * ler — é o gesto de quem já decidiu antes de a frase aparecer. Digitar o nome do
 * projeto obriga a olhar para qual projeto é, que é exatamente o erro que essas
 * operações cometem: apagar o pinout do projeto certo do jeito errado é raro;
 * apagar o do projeto errado é o que acontece.
 *
 * A frase de consequência vem antes do campo, e diz **o tamanho** — "apaga os 412
 * pinouts" é outra coisa que "apaga os pinouts".
 *
 * Não é para tudo. Operações reversíveis — limpar cache, refazer um desenho —
 * pedem confirmação simples; gastar este gesto nelas ensina a digitar no
 * automático, e aí ele deixa de proteger o que importa.
 */

import { useState, type ReactNode } from "react"

export function ConfirmacaoDigitada({
  alvo, oQueAcontece, rotuloDoBotao, ocupado, aoConfirmar, aoCancelar, impedido,
}: {
  /** O texto que a pessoa tem de digitar — o nome do projeto, do veículo, do motor. */
  alvo: string
  /** O que vai acontecer, com o tamanho. */
  oQueAcontece: ReactNode
  rotuloDoBotao: string
  ocupado?: boolean
  aoConfirmar: () => void
  aoCancelar: () => void
  /** Quando a operação está barrada por outro motivo — a frase explica qual. */
  impedido?: ReactNode
}) {
  const [digitado, setDigitado] = useState("")

  // Comparacao frouxa de proposito: espaco sobrando e caixa nao sao o ponto, e
  // ninguem digita 40 caracteres por acidente.
  const confere = digitado.trim().toLocaleLowerCase() === alvo.trim().toLocaleLowerCase()

  return (
    <div style={{
      padding: 12, borderRadius: 7, border: "1px solid var(--erro)",
      background: "color-mix(in srgb, var(--erro) 5%, transparent)",
      maxWidth: "76ch",
    }}>
      <p style={{ margin: "0 0 9px", fontSize: 12, lineHeight: 1.6 }}>{oQueAcontece}</p>

      {impedido ? (
        <p style={{
          margin: "0 0 9px", padding: "7px 9px", borderRadius: 5, fontSize: 11.5,
          lineHeight: 1.55, background: "var(--surface2)", color: "var(--erro)",
        }}>
          {impedido}
        </p>
      ) : (
        <>
          <label style={{ display: "block", fontSize: 11, color: "var(--txt2)", marginBottom: 4 }}>
            Para confirmar, digite <b className="mono">{alvo}</b>
          </label>
          <input
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
            autoFocus
            placeholder={alvo}
            style={{
              width: "100%", maxWidth: 380, padding: "6px 9px", fontSize: 12.5,
              border: "1px solid " + (confere ? "var(--ok, #2e7d32)" : "var(--line)"),
              borderRadius: 6, background: "var(--surface)", color: "var(--txt)",
              marginBottom: 10,
            }}
          />
        </>
      )}

      <div style={{ display: "flex", gap: 7 }}>
        <button
          onClick={aoConfirmar}
          disabled={!confere || ocupado || Boolean(impedido)}
          style={{
            height: 28, padding: "0 13px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            border: 0, cursor: confere && !ocupado && !impedido ? "pointer" : "default",
            background: confere && !impedido ? "var(--erro)" : "var(--surface2)",
            color: confere && !impedido ? "#fff" : "var(--txt3)",
          }}
        >
          {ocupado ? "Fazendo…" : rotuloDoBotao}
        </button>
        <button
          onClick={aoCancelar}
          disabled={ocupado}
          style={{
            height: 28, padding: "0 13px", borderRadius: 7, fontSize: 12.5,
            border: "1px solid var(--line)", background: "var(--surface)",
            color: "var(--txt)", cursor: "pointer",
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}
